import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import HeroSection from "./HeroSection";
import IntroText from "./IntroText";
import productIntroImage1 from "../../../assets/img/homepage/1.png";
import productIntroImage2 from "../../../assets/img/homepage/2.png";
import productIntroImage3 from "../../../assets/img/homepage/3.png";
import ProductIntroCard from "./ProductIntroCard";
import { Space } from "antd";
import { useMediaQuery } from "react-responsive";
import FeaturedProductList from "./FeaturedProductList";
import Applications from "./Applications";
import ContactUs from "./ContactUs";
import useApi from "../../../hooks/useApi";
import { API_METHODS, BLANK_VALUE } from "../../../constants";

const HomePage = () => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

  const [getSeoDataResponse, getSeoDataRequest] = useApi(
    `/website/static-seo?page_identifier=Home`,
    null,
    API_METHODS.GET
  );

  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    getSeoDataRequest();
  }, []);

  useEffect(() => {
    if (getSeoDataResponse.data && !getSeoDataResponse.isLoading) {
      const data =
        getSeoDataResponse.data !== BLANK_VALUE
          ? getSeoDataResponse.data
          : null;
      setSeoData(data);
    }
  }, [getSeoDataResponse]);

  // SEO Helper Functions
  const generateOrganizationSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Cenvance Innovations Ltd",
      url: "https://cenvance.com",
      logo: "https://cenvance.com/logo.png",
      description:
        "We provide high-quality solutions that empower research laboratories, hospitals, and diagnostic centers worldwide.",
      sameAs: [
        "https://www.facebook.com/cenvance",
        "https://www.linkedin.com/company/cenvance",
        "https://twitter.com/cenvance",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        email: "info@cenvance.com",
        areaServed: "Worldwide",
      },
    };
  };

  const generateWebsiteSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Cenvance Innovations Ltd",
      url: "https://cenvance.com",
      description:
        seoData?.meta_description ||
        "We provide high-quality solutions that empower research laboratories, hospitals, and diagnostic centers worldwide.",
      publisher: {
        "@type": "Organization",
        name: "Cenvance Innovations Ltd",
      },
      potentialAction: {
        "@type": "SearchAction",
        target: "https://cenvance.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    };
  };

  const generateWebPageSchema = () => {
    if (!seoData) return null;

    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: seoData.page_identifier || "Home",
      description: seoData.meta_description,
      url: "https://cenvance.com",
      inLanguage: "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: "Cenvance Innovations Ltd",
        url: "https://cenvance.com",
      },
      about: {
        "@type": "Thing",
        name: "Laboratory Equipment and Medical Devices",
      },
      keywords: seoData.meta_keywords,
    };
  };

  return (
    <>
      {/* SEO HEAD SECTION */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>
          {seoData?.meta_title ||
            "Cenvance - Laboratory Equipment & Medical Devices"}
        </title>
        <meta
          name="description"
          content={
            seoData?.meta_description ||
            "We provide high-quality solutions that empower research laboratories, hospitals, and diagnostic centers worldwide."
          }
        />
        {seoData?.meta_keywords && (
          <meta name="keywords" content={seoData.meta_keywords} />
        )}
        <meta name="robots" content={seoData?.meta_robots || "index, follow"} />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href={seoData?.canonical_url || "https://cenvance.com/"}
        />

        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cenvance Innovations Ltd" />
        <meta
          property="og:url"
          content={seoData?.canonical_url || "https://cenvance.com/"}
        />
        <meta
          property="og:title"
          content={
            seoData?.og_title ||
            seoData?.meta_title ||
            "Cenvance Innovations Ltd"
          }
        />
        <meta
          property="og:description"
          content={
            seoData?.og_description ||
            seoData?.meta_description ||
            "We provide high-quality solutions that empower research laboratories"
          }
        />
        {seoData?.og_image && (
          <meta property="og:image" content={seoData.og_image} />
        )}

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            seoData?.twitter_title ||
            seoData?.meta_title ||
            "Cenvance Innovations Ltd"
          }
        />
        <meta
          name="twitter:description"
          content={
            seoData?.twitter_description ||
            seoData?.meta_description ||
            "We provide high-quality solutions"
          }
        />
        {seoData?.twitter_image && (
          <meta name="twitter:image" content={seoData.twitter_image} />
        )}

        {/* Structured Data Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(generateOrganizationSchema())}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(generateWebsiteSchema())}
        </script>

        {seoData && (
          <script type="application/ld+json">
            {JSON.stringify(generateWebPageSchema())}
          </script>
        )}

        {/* Additional Schema if exists */}
        {seoData?.additional_schema && (
          <script type="application/ld+json">
            {typeof seoData.additional_schema === "string"
              ? seoData.additional_schema
              : JSON.stringify(seoData.additional_schema)}
          </script>
        )}
      </Helmet>

      <HeroSection isTabletOrMobile={isTabletOrMobile} />
      <IntroText isTabletOrMobile={isTabletOrMobile} />

      <Space size={48} direction="vertical" style={{ width: "100%" }}>
        <ProductIntroCard
          isTabletOrMobile={isTabletOrMobile}
          image={productIntroImage1}
          title="Laboratory Equipement"
          text={
            <>
              Forge the future of science with presion tools <br />
              built for breakthrough innovation.
            </>
          }
          imagePlacement="left"
          viewProductsLink={"/lab-equipment/laboratory-shakers"}
        />
        <ProductIntroCard
          isTabletOrMobile={isTabletOrMobile}
          image={productIntroImage2}
          title="Medical Devices"
          text={
            <>
              Revolutionize with devices engineeried to <br />
              trasform health and inspire trust.
            </>
          }
          imagePlacement="right"
          viewProductsLink={"/medical-devices/laboratory-baths"}
        />
        <ProductIntroCard
          isTabletOrMobile={isTabletOrMobile}
          image={productIntroImage3}
          title="Laboratory Supplies"
          text={
            <>
              Ignite discovery with essential that <br />
              empower every bold experiment
            </>
          }
          imagePlacement="left"
          viewProductsLink={"/lab-supplies/vacuum-pumps"}
        />
      </Space>

      <FeaturedProductList isTabletOrMobile={isTabletOrMobile} />

      <Applications />
      <ContactUs isTabletOrMobile={isTabletOrMobile} />
    </>
  );
};

export default HomePage;
